/**
 * TELV1-186 — Frontend mirror of the backend's
 * PlaceholderReplacementService logic, used by the EmbedCodeGenerator
 * %%var%% inspector + "Test piping" preview.
 *
 * Regex matches the backend exactly: /%%(\w+)%%/g. Substitution is
 * case-insensitive on the placeholder key (same as backend's
 * normalizeMetadataKeys → toLowerCase). When a placeholder has no
 * matching sample value, the %% delimiters are stripped but the
 * placeholder name remains as plain text — same graceful degradation
 * the production conversation flow does.
 */

// ---------------------------------------------------------------------------
// Survey-platform config — drives EmbedCodeGenerator's platform selector,
// participant_id URL-syntax defaults, and paste-location hints.
// (Helper-script generation is deferred to TELV1-426; only the config stays.)
// ---------------------------------------------------------------------------

export type Platform = 'qualtrics' | 'decipher' | 'dimensions' | 'confirmit' | 'nfield' | 'vanilla';

export type PlatformTier = 'verified' | 'best-effort' | 'generic';

/**
 * TELV1-487 — honest support tiering, surfaced in the generator UI. The
 * descriptions are the honesty backstop: "best-effort" platforms carry
 * instructions from the platform's official documentation that Tellet has not
 * yet verified end-to-end, and the copy must say so.
 */
export const TIER_COPY: Record<PlatformTier, { label: string; description: string }> = {
  verified: {
    label: 'Verified',
    description: 'Verified — this integration has been tested end-to-end with Tellet.',
  },
  'best-effort': {
    label: 'Docs-based',
    description:
      "Docs-based — instructions follow the platform's official documentation, not yet verified end-to-end by Tellet.",
  },
  generic: {
    label: 'Generic',
    description: 'Generic — works in any page that allows custom HTML and JavaScript.',
  },
};

export interface PlatformConfig {
  id: Platform;
  label: string;
  /** Respondent-ID URL-parameter syntax for this platform */
  urlSyntax: string;
  tier: PlatformTier;
  /**
   * Where to paste the generated snippet on this platform. Single source of
   * truth for the generator's hint line (TELV1-487 — the previous
   * component-local map shipped locations verified wrong against the
   * platforms' own docs: Qualtrics strips <script> from question HTML,
   * Dimensions Labels only render single-line HTML, Confirmit Script Objects
   * are logic-only and can't render).
   */
  pasteHint: string;
  /**
   * Builds a platform-native piped-variable example for a %%var%% name, shown
   * as the mapping-input placeholder. Absent = syntax not verified for this
   * platform; the UI falls back to a neutral hint. A function (not a token
   * template) so the Qualtrics `${...}` shape needs no no-template-curly
   * escaping or sentinel replacement.
   */
  pipeExample?: (varName: string) => string;
}

export const PLATFORMS: readonly PlatformConfig[] = [
  {
    id: 'decipher',
    label: 'Forsta (Decipher)',
    // eslint-disable-next-line no-template-curly-in-string
    urlSyntax: '${uuid}',
    // Verified end-to-end (XML-editor paste; inline JS executes in-survey).
    tier: 'verified',
    pasteHint:
      'Paste inside a <html where="survey"><![CDATA[ ... ]]> element in the XML editor, or via Descriptive Content → Source Code.',
    pipeExample: (varName) => `[pipe: ${varName}]`,
  },
  {
    id: 'dimensions',
    label: 'UNICOM Intelligence (Dimensions)',
    urlSyntax: '{IOM.Info.Serial}',
    tier: 'best-effort',
    pasteHint:
      "Render via a custom QuestionTemplate. An info-item Label won't work — Labels only render HTML when the whole tag is on a single line, and this snippet is multi-line.",
    pipeExample: (varName) => `{#${varName}}`,
  },
  {
    id: 'nfield',
    label: 'Nfield',
    urlSyntax: '{respondentKey}',
    // TELV1-488 — in-page embed via ODIN << >> script brackets, tested
    // successfully in-house (2026-07-14). NOT App-in-the-Middle: AITM is a
    // full-page redirect, which defeats embedding.
    tier: 'verified',
    pasteHint:
      'Paste the whole block — including the << >> brackets — into your CAWI question text in ODIN. It runs in-page (no redirect). One Tellet block per survey page.',
  },
  {
    id: 'confirmit',
    label: 'Confirmit',
    urlSyntax: '^respid^',
    tier: 'best-effort',
    pasteHint:
      "Requires author JavaScript (e.g. an ss:includeJS template include). Script Objects run logic only and can't render HTML — confirm the exact paste location in your instance and test in-tool.",
    pipeExample: (varName) => `^${varName}^`,
  },
  {
    id: 'qualtrics',
    label: 'Qualtrics',
    // eslint-disable-next-line no-template-curly-in-string
    urlSyntax: '${e://Field/ResponseID}',
    tier: 'best-effort',
    pasteHint:
      "Two parts: paste the iframe into the question's HTML View, and the script into Question behavior → JavaScript (replace the editor's entire contents). Custom JavaScript requires a Qualtrics license that includes it.",
    pipeExample: (varName) => `\${e://Field/${varName}}`,
  },
  {
    id: 'vanilla',
    label: 'Vanilla JS (other / custom)',
    urlSyntax: '',
    tier: 'generic',
    pasteHint: 'Paste into any HTML field that supports iframes and inline JavaScript.',
  },
] as const;

// ---------------------------------------------------------------------------

export const PLACEHOLDER_REGEX = /%%(\w+)%%/g;

export function extractPlaceholderNames(...texts: (string | undefined | null)[]): string[] {
  const seen = new Set<string>();
  texts.forEach((text) => {
    if (!text) return;
    Array.from(text.matchAll(PLACEHOLDER_REGEX)).forEach((m) => {
      seen.add(m[1].toLowerCase());
    });
  });
  return Array.from(seen).sort();
}

export function substitutePlaceholders(text: string, values: Record<string, string>): string {
  if (!text) return text;

  const normalized: Record<string, string> = {};
  Object.entries(values).forEach(([k, v]) => {
    if (v != null && v !== '') normalized[k.toLowerCase()] = v;
  });

  return text.replace(PLACEHOLDER_REGEX, (_full, name: string) => {
    const key = name.toLowerCase();
    if (key in normalized) return normalized[key];
    return name;
  });
}
